/**
 * PolishInput — Collapsible section for pasting lyrics to polish.
 * Lives inside SunoConfigTab below the Parliament Write button.
 *
 * The title bar shows the sound settings the run will use (genre, vocal, instruments, solo, mix) and
 * opens a modal to change them. A song handed over from the Vault or Sessions lands here too.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, ChevronDown, X, SlidersHorizontal, FileText } from 'lucide-react';
import { Button } from '@/components/ui';
import { Badge } from '@/components/ui';
import { TextArea } from '@/components/ui/Form';
import { ForgeSoundModal, useForgeSummary } from './ForgeSoundModal';
import { usePolishHandoff, armScaffold } from '@/utils/polishHandoff';

interface PolishInputProps {
  onPolish: (lyrics: string) => void;
  isPolishing: boolean;
  disabled: boolean;
  /** Current Musical Blend selection, shown as the Genre chip and edited in the sound modal. */
  activeBlends?: string[];
  onBlendChange?: (blends: string[]) => void;
}

export const PolishInput: React.FC<PolishInputProps> = ({
  onPolish, isPolishing, disabled, activeBlends = [], onBlendChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [lyrics, setLyrics] = useState('');
  const [soundOpen, setSoundOpen] = useState(false);
  const chips = useForgeSummary(activeBlends);
  const handoff = usePolishHandoff();
  const lastNonce = useRef<number | null>(null);

  // A song sent from the Vault or Sessions fills the box and opens it. Once per send.
  useEffect(() => {
    if (!handoff || handoff.nonce === lastNonce.current) return;
    lastNonce.current = handoff.nonce;
    setLyrics(handoff.lyrics);
    setIsOpen(true);
  }, [handoff]);

  const charCount = lyrics.length;
  const fromHandoff = !!handoff && handoff.lyrics.trim() === lyrics.trim();

  const handlePolish = () => {
    const text = lyrics.trim();
    if (text.length < 20) return;
    armScaffold(text);
    onPolish(text);
  };

  const handleClear = () => {
    setLyrics('');
  };

  return (
    <div className="border border-slate-800 rounded-xl overflow-hidden">
      {/* Title bar: toggle on the left, sound settings on the right */}
      <div className="flex items-center gap-2 pr-3 hover:bg-slate-800/30 transition-colors">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex-1 flex items-center justify-between px-4 py-2.5 text-left"
          type="button"
        >
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-amber-400" />
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
              Parliament Polish
            </span>
            {lyrics.trim().length > 0 && !isOpen && (
              <Badge variant="default-muted" size="xs">{charCount} chars</Badge>
            )}
          </div>
          <ChevronDown
            size={14}
            className={`text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
        <button
          type="button"
          onClick={() => setSoundOpen(true)}
          className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-2.5 py-1 text-[11px] font-bold text-slate-300 hover:border-accent/60 hover:text-accent transition-colors"
          title="Choose the genre, vocal, instrument, solo and mix settings for this polish"
        >
          <SlidersHorizontal size={12} />
          Sound
        </button>
      </div>

      {/* What the run will use, one chip per forge. Clicking any opens the modal. */}
      <div className="flex flex-wrap gap-1.5 px-4 pb-2.5">
        {chips.map(chip => (
          <button
            key={chip.key}
            type="button"
            onClick={() => setSoundOpen(true)}
            className={`rounded-full border px-2 py-0.5 text-[10px] transition-colors hover:border-accent/60 ${
              chip.value ? 'border-slate-700 text-slate-300' : 'border-slate-800 text-slate-600'
            }`}
            title={chip.value ? `${chip.label}: ${chip.value}` : `${chip.label}: not set`}
          >
            <span className="font-bold uppercase tracking-wider text-slate-500">{chip.label}</span>{' '}
            <span className="max-w-[10rem] inline-block truncate align-bottom">{chip.value ?? '—'}</span>
          </button>
        ))}
      </div>

      {/* Expanded Content */}
      {isOpen && (
        <div className="px-4 pb-4 space-y-3 animate-in slide-in-from-top-2 duration-200">
          <p className="text-[11px] text-slate-500">
            Paste existing lyrics below. 4 specialist editors will review and suggest improvements,
            then a synthesizer merges the best changes into a polished version.
          </p>

          {fromHandoff && handoff && (
            <div className="flex items-start gap-2 rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-[11px] text-slate-300">
              <FileText size={14} className="mt-0.5 shrink-0 text-accent" />
              <span>
                Loaded from your song <strong className="text-slate-100">{handoff.title}</strong>.
                {handoff.scaffold
                  ? ' Its Studio Team arrangement cues and vocal dossier are set aside and will be put back on the polished version.'
                  : ' Vocal and production layers are rebuilt from the sound settings above.'}
              </span>
            </div>
          )}

          <div className="relative">
            <TextArea
              rows={8}
              placeholder="[Verse 1]&#10;Paste your lyrics here...&#10;&#10;[Chorus]&#10;The chorus goes here..."
              value={lyrics}
              onChange={(e) => setLyrics(e.target.value)}
              className="bg-slate-950 rounded-xl text-[12px]"
            />
            {lyrics.length > 0 && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute top-2 right-2 text-slate-600 hover:text-slate-400 transition-colors"
                title="Clear"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between">
            <Badge
              variant={charCount > 5000 ? 'danger' : 'default-muted'}
              size="xs"
            >
              {charCount} / 5000 chars
            </Badge>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handlePolish}
              disabled={disabled || lyrics.trim().length < 20 || charCount > 5000}
              isLoading={isPolishing}
              loadingText="Polishing..."
              leftIcon={<Sparkles size={14} />}
              className="border-amber-500/50 text-amber-400 hover:bg-amber-900/20"
            >
              Polish Lyrics
            </Button>
          </div>
        </div>
      )}

      <ForgeSoundModal
        isOpen={soundOpen}
        onClose={() => setSoundOpen(false)}
        activeBlends={activeBlends}
        onBlendChange={onBlendChange}
      />
    </div>
  );
};
